import "./InteractiveTableView.css";
import { Button } from "@ui/Button";
import { toast } from "@ui/molecules/Toasts/toast";
import { HStack, VStack } from "@ui/Stack";
import { Tooltip, TooltipTrigger } from "@ui/Tooltip";
import type { JSX } from "solid-js";
import {
	createEffect,
	createMemo,
	createSignal,
	For,
	onCleanup,
	onMount,
	Show,
} from "solid-js";
import { observeBleed } from "../utils/bleed";
import {
	isRichTableCell,
	type ParsedTableCell,
	tableCellText,
} from "../utils/parseTableElement";
import { tableClipboardContent } from "../utils/tableClipboard";
import {
	detectColumnFormats,
	type TableColumnFormat,
} from "../utils/tableColumnFormats";
import { fitWithin, shareSpareWidth, sum } from "../utils/tableColumnWidths";

type TableCellValue = string | ParsedTableCell;

type InteractiveTableViewProps = {
	/** Heading that titled the table in the markdown, shown in the toolbar. */
	heading?: HTMLElement;
	headers: TableCellValue[];
	rows: TableCellValue[][];
	truncationMessage?: string;
	id?: string;
	class?: string;
	theme?: "standard" | "borderless";
	/** Offer to spread the table across the conversation it's shown in. */
	allowFullWidth?: boolean;
};

const mountRichCellNodes = (
	cell: HTMLElement,
	nodes: Node[],
	before?: ChildNode | null,
) => {
	for (const node of nodes) {
		cell.insertBefore(node, before ?? null);
	}
};

/** `{ date: true }` etc., so the detected format becomes the cell's class. */
const formatClassList = (format: TableColumnFormat | undefined) =>
	format ? { [format]: true } : {};

/** Lines of text in `cell`, counted from the line boxes of its text. */
const countLines = (cell: Element) => {
	const tops = new Set<number>();
	const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
	const range = document.createRange();
	for (let node = walker.nextNode(); node; node = walker.nextNode()) {
		range.selectNodeContents(node);
		for (const rect of range.getClientRects()) {
			if (rect.height > 0) {
				tops.add(Math.round(rect.top));
			}
		}
	}
	return tops.size;
};

/**
 * How much further down `el`'s own top margin puts it than the block above
 * would on its own. The two margins collapse into the larger, so a negative
 * margin moves `el` from where the block above's margin alone leaves it, not
 * from where it sits.
 */
const measureOwnMarginTop = (el: HTMLElement) => {
	const top = el.getBoundingClientRect().top;
	const inline = el.style.marginTop;
	el.style.marginTop = "0px";
	const withoutOwn = el.getBoundingClientRect().top;
	el.style.marginTop = inline;
	return top - withoutOwn;
};

/** Width the table may occupy before the wrapper has to scroll. */
const wrapperContentWidth = (el: HTMLElement) => {
	const style = getComputedStyle(el);
	// Narrow chat layouts reserve the right gutter with a flex `::after` spacer
	// rather than padding, so it scrolls along with the table.
	const spacer = parseFloat(getComputedStyle(el, "::after").flexBasis) || 0;
	return (
		el.clientWidth -
		parseFloat(style.paddingLeft) -
		parseFloat(style.paddingRight) -
		spacer
	);
};

const TableBodyCell = (props: {
	cell: TableCellValue;
	style?: JSX.CSSProperties;
	format?: TableColumnFormat;
}) => {
	const mountCell = (td: HTMLTableCellElement) => {
		if (isRichTableCell(props.cell)) {
			mountRichCellNodes(td, props.cell.nodes);
		}
	};

	return (
		<td
			ref={mountCell}
			style={props.style}
			classList={formatClassList(props.format)}
		>
			<Show when={!isRichTableCell(props.cell)}>
				{tableCellText(props.cell)}
			</Show>
		</td>
	);
};

export const InteractiveTableView = (props: InteractiveTableViewProps) => {
	/** Unwrapped column widths; `null` until the user first sizes a column. */
	const [naturalWidths, setNaturalWidths] = createSignal<number[] | null>(null);
	/** Columns the user has dragged, kept separately for each display mode. */
	const [userWidths, setUserWidths] = createSignal<{
		wrapped: (number | undefined)[];
		unwrapped: (number | undefined)[];
	}>({ wrapped: [], unwrapped: [] });
	const [fittedWrapWidths, setFittedWrapWidths] = createSignal<number[]>([]);
	const [fittedFullWidths, setFittedFullWidths] = createSignal<number[]>([]);
	const [wrapText, setWrapText] = createSignal(false);
	const [resizing, setResizing] = createSignal<number | null>(null);
	const [hoveredHandle, setHoveredHandle] = createSignal<number | null>(null);
	const [dragHeight, setDragHeight] = createSignal<number | null>(null);
	const [hasXScroll, setHasXScroll] = createSignal(false);
	/** The conversation column a full-width table spreads across, if any. */
	const [fullWidthBounds, setFullWidthBounds] = createSignal<HTMLElement>();
	const [fullWidth, setFullWidth] = createSignal(false);
	const [fullWidthTransition, setFullWidthTransition] = createSignal(false);
	const [bleed, setBleed] = createSignal({ left: 0, right: 0 });
	let tableRef: HTMLTableElement | undefined;
	let wrapperRef: HTMLDivElement | undefined;
	let fittedForWidth: number | null = null;
	/** See {@link measureOwnMarginTop}; full width reduces it by its padding. */
	let ownMarginTop = 0;

	const MAX_INITIAL_WIDTH = 20 * 16; // 20rem in px
	const MIN_WRAPPED_WIDTH = 10 * 16; // 10rem in px

	const columnFormats = createMemo(() =>
		detectColumnFormats(props.rows, props.headers.length),
	);

	/**
	 * Cell widths including padding, as cells are `border-box` (see
	 * reset.css), or `null` to leave layout to CSS.
	 */
	const columnWidths = createMemo(() => {
		const natural = naturalWidths();
		if (!natural) return null;
		// Full width is fitted once the wider wrapper has been laid out, so the
		// usual widths stand in until then.
		const auto = wrapText()
			? fittedWrapWidths()
			: natural.map((w, i) =>
					fullWidth()
						? (fittedFullWidths()[i] ?? Math.min(w, MAX_INITIAL_WIDTH))
						: Math.min(w, MAX_INITIAL_WIDTH),
				);
		const user = wrapText() ? userWidths().wrapped : userWidths().unwrapped;
		return natural.map((_, i) => user[i] ?? auto[i]);
	});

	const syncHorizontalScrollGutter = () => {
		const el = wrapperRef;
		if (!el) {
			return;
		}
		const needsGutter = el.scrollWidth > el.clientWidth;
		if (needsGutter !== hasXScroll()) {
			setHasXScroll(needsGutter);
		}
	};

	/**
	 * Measures each column with its text on one line. Rows can arrive after
	 * any earlier measurement, so this reruns whenever a width is derived from
	 * it. It lays out a hidden copy at max-content: the live table has widths
	 * pinned on its cells and can be squeezed by the space around it.
	 */
	const measureNaturalWidths = () => {
		if (!tableRef) return;
		const copy = tableRef.cloneNode(true) as HTMLTableElement;
		copy.classList.remove("interactive-table--wrap-text");
		copy.style.position = "absolute";
		copy.style.visibility = "hidden";
		copy.style.width = "max-content";
		for (const cell of copy.querySelectorAll<HTMLElement>("th, td")) {
			cell.style.width = "";
			cell.style.minWidth = "";
			cell.style.maxWidth = "none";
		}
		tableRef.after(copy);
		const measured = Array.from(
			copy.querySelectorAll("thead th"),
			(th) => th.getBoundingClientRect().width,
		);
		copy.remove();
		setNaturalWidths(measured);
	};

	const setUserWidth = (colIndex: number, width: number) => {
		const mode = wrapText() ? "wrapped" : "unwrapped";
		setUserWidths((prev) => {
			const next = [...prev[mode]];
			next[colIndex] = width;
			return { ...prev, [mode]: next };
		});
	};

	/**
	 * Turns wrapping on and sizes the columns the user hasn't dragged in wrap
	 * mode: each at most `MAX_INITIAL_WIDTH`, shrunk to fit the wrapper if need
	 * be, then widened into any width left over by the columns that wrap.
	 * Only rerun on mode or container changes, never mid-drag, so dragging one
	 * column doesn't reflow the others under the pointer.
	 */
	const layOutWrapped = () => {
		const natural = naturalWidths();
		if (!natural || !tableRef || !wrapperRef) return;

		const user = userWidths().wrapped;
		const free = natural.map((_, i) => i).filter((i) => user[i] === undefined);
		const pinned = sum(natural.map((_, i) => user[i] ?? 0));

		const available = wrapperContentWidth(wrapperRef);
		const budget = available - pinned;
		const unwrapped = free.map((i) => Math.ceil(natural[i]));
		const preferred = unwrapped.map((w) => Math.min(w, MAX_INITIAL_WIDTH));
		const fitted = fitWithin(
			preferred,
			preferred.map((p) => Math.min(p, MIN_WRAPPED_WIDTH)),
			budget,
		);

		const apply = (widths: number[]) => {
			const next = [...natural];
			free.forEach((col, j) => {
				next[col] = widths[j];
			});
			setFittedWrapWidths(next);
		};
		fittedForWidth = available;
		apply(fitted);
		setWrapText(true);

		const spare = budget - sum(fitted);
		if (spare < 1) return;

		// Lines can only be counted once the browser has laid out the wrapped
		// text, which the signals above have just caused.
		const rows = Array.from(tableRef.rows);
		const area = free.map((col, j) =>
			fitted[j] < unwrapped[j]
				? fitted[j] *
					sum(
						rows.map((row) =>
							row.cells[col] ? countLines(row.cells[col]) : 0,
						),
					)
				: 0,
		);
		apply(shareSpareWidth(fitted, unwrapped, area, spare));
	};

	/**
	 * Sizes the unwrapped columns the user hasn't dragged for full width: the
	 * extra room goes to the columns `MAX_INITIAL_WIDTH` cuts short, up to
	 * their whole text, but none is cut shorter than it would be otherwise.
	 */
	const layOutFullWidthUnwrapped = () => {
		const natural = naturalWidths();
		if (!natural || !wrapperRef) return;

		const user = userWidths().unwrapped;
		const free = natural.map((_, i) => i).filter((i) => user[i] === undefined);
		const available = wrapperContentWidth(wrapperRef);
		const unwrapped = free.map((i) => Math.ceil(natural[i]));
		const fitted = fitWithin(
			unwrapped,
			unwrapped.map((w) => Math.min(w, MAX_INITIAL_WIDTH)),
			available - sum(natural.map((_, i) => user[i] ?? 0)),
		);

		const next = [...natural];
		free.forEach((col, j) => {
			next[col] = fitted[j];
		});
		fittedForWidth = available;
		setFittedFullWidths(next);
	};

	const toggleWrapText = () => {
		measureNaturalWidths();
		if (wrapText()) {
			setWrapText(false);
			if (fullWidth()) layOutFullWidthUnwrapped();
			return;
		}
		layOutWrapped();
	};

	const endFullWidthTransitionWhenSettled = () => {
		if (!wrapperRef?.parentElement?.getAnimations().length) {
			setFullWidthTransition(false);
		}
	};

	const toggleFullWidth = () => {
		measureNaturalWidths();
		const container = wrapperRef?.parentElement;
		// Until shrinking has finished the margin is still full width's, not at rest.
		if (!fullWidth() && !fullWidthTransition() && container) {
			ownMarginTop = measureOwnMarginTop(container);
		}
		setFullWidthTransition(true);
		setFullWidth(!fullWidth());
		// No transition starts when the margins don't change (e.g. the card
		// already spans the conversation) or motion is reduced, so nothing
		// would end it.
		endFullWidthTransitionWhenSettled();
	};

	const copyTable = () => {
		const { plainText, html } = tableClipboardContent(
			props.headers,
			props.rows,
		);
		void navigator.clipboard.write([
			new ClipboardItem({
				"text/plain": new Blob([plainText], { type: "text/plain" }),
				"text/html": new Blob([html], { type: "text/html" }),
			}),
		]);
		toast.success({ title: "Copied to clipboard" });
	};

	const onResizeReset = (colIndex: number, e: MouseEvent) => {
		e.preventDefault();
		measureNaturalWidths();
		setUserWidth(colIndex, naturalWidths()![colIndex]);
	};

	const beginDrag = (colIndex: number, startX: number) => {
		measureNaturalWidths();
		const widths = columnWidths()!;
		const startWidth = widths[colIndex];
		const maxWidth = naturalWidths()![colIndex];

		setResizing(colIndex);
		setDragHeight(tableRef!.getBoundingClientRect().height);

		const updateWidth = (currentX: number) => {
			const delta = currentX - startX;
			setUserWidth(
				colIndex,
				Math.min(maxWidth, Math.max(30, startWidth + delta)),
			);
		};

		const endResize = () => {
			setResizing(null);
			setHoveredHandle(null);
		};

		return { updateWidth, endResize };
	};

	const onResizeStart = (colIndex: number, e: MouseEvent) => {
		e.preventDefault();
		const { updateWidth, endResize } = beginDrag(colIndex, e.clientX);

		document.body.style.cursor = "col-resize";
		document.body.style.userSelect = "none";

		const onMouseMove = (moveEvent: MouseEvent) =>
			updateWidth(moveEvent.clientX);

		const onMouseUp = () => {
			endResize();
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
		};

		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
	};

	const onTouchResizeStart = (colIndex: number, e: TouchEvent) => {
		e.preventDefault();
		const { updateWidth, endResize } = beginDrag(
			colIndex,
			e.touches[0].clientX,
		);

		const onTouchMove = (moveEvent: TouchEvent) => {
			moveEvent.preventDefault();
			updateWidth(moveEvent.touches[0].clientX);
		};

		const onTouchEnd = () => {
			endResize();
			document.removeEventListener("touchmove", onTouchMove);
			document.removeEventListener("touchend", onTouchEnd);
		};

		document.addEventListener("touchmove", onTouchMove, { passive: false });
		document.addEventListener("touchend", onTouchEnd);
	};

	onMount(() => {
		const el = wrapperRef;
		if (!el) {
			return;
		}
		const observer = new ResizeObserver(() => {
			// Streamed tables are rendered before they're added to the page, so
			// the conversation can only be looked up once there's a layout.
			if (props.allowFullWidth && !fullWidthBounds()) {
				const bounds = el.closest<HTMLElement>(".chat-layout__conversation");
				if (bounds) setFullWidthBounds(bounds);
			}
			// The table itself is observed too, and refitting resizes it, so only
			// a change in the space available may trigger another fit.
			if (wrapperContentWidth(el) !== fittedForWidth) {
				if (wrapText()) {
					layOutWrapped();
				} else if (fullWidth()) {
					layOutFullWidthUnwrapped();
				}
			}
			syncHorizontalScrollGutter();
		});
		observer.observe(el);
		if (tableRef) {
			observer.observe(tableRef);
		}
		syncHorizontalScrollGutter();
		onCleanup(() => observer.disconnect());

		const container = el.parentElement;
		const onTransitionDone = (e: TransitionEvent) => {
			if (e.target === container) endFullWidthTransitionWhenSettled();
		};
		container?.addEventListener("transitionend", onTransitionDone);
		container?.addEventListener("transitioncancel", onTransitionDone);
		onCleanup(() => {
			container?.removeEventListener("transitionend", onTransitionDone);
			container?.removeEventListener("transitioncancel", onTransitionDone);
		});
	});

	createEffect(() => {
		const bounds = fullWidthBounds();
		const container = wrapperRef?.parentElement;
		if (!fullWidth() || !bounds || !container) return;
		onCleanup(observeBleed(container, bounds, setBleed));
	});

	onCleanup(() => {
		document.body.style.cursor = "";
		document.body.style.userSelect = "";
	});

	const colStyle = (index: number) => {
		const widths = columnWidths();
		if (!widths) return undefined;
		return {
			width: `${widths[index]}px`,
			"min-width": `${widths[index]}px`,
			"max-width": `${widths[index]}px`,
		};
	};

	/**
	 * Wrapping text gives every column a small minimum, so the table would
	 * squeeze the cells to fit the wrapper, overriding their widths. Sizing
	 * the table to their sum lets the user drag it wider than the wrapper.
	 */
	const tableStyle = () => {
		const widths = columnWidths();
		return widths ? { width: `${sum(widths)}px` } : undefined;
	};

	/**
	 * Full width pulls the container out to the conversation's edges, and up
	 * by the padding it gains, from wherever the page put it.
	 */
	const containerStyle = () =>
		fullWidth()
			? {
					"margin-left": `${-bleed().left}px`,
					"margin-right": `${-bleed().right}px`,
					"margin-top": `calc(${ownMarginTop}px - var(--interactive-table-full-width-inset-top))`,
				}
			: undefined;

	return (
		<VStack
			id={props.id}
			spacing="small"
			class={`interactive-table-container ${props.class ?? ""} ${props.theme ? `interactive-table-container--${props.theme}` : ""} ${fullWidth() ? "interactive-table-container--full-width" : ""} ${fullWidthTransition() ? "interactive-table-container--full-width-transition" : ""}`}
			style={containerStyle()}
		>
			<HStack
				justify={props.heading ? "space-between" : "end"}
				align="center"
				spacing="medium"
				class="interactive-table-toolbar"
			>
				{props.heading}
				<HStack spacing="large">
					<HStack spacing="small">
						<TooltipTrigger>
							<Button
								variant="text"
								theme="monochrome"
								shape="square"
								symbol="content_copy"
								aria-label="Copy table"
								onClick={copyTable}
							/>
							<Tooltip>Copy table</Tooltip>
						</TooltipTrigger>
					</HStack>
					<HStack spacing="small">
						<TooltipTrigger>
							<Button
								variant="text"
								theme="monochrome"
								shape="square"
								symbol="wrap_text"
								aria-label="Wrap text"
								aria-pressed={wrapText()}
								onClick={toggleWrapText}
							/>
							<Tooltip>Wrap text</Tooltip>
						</TooltipTrigger>
						<Show when={fullWidthBounds()}>
							<TooltipTrigger>
								<Button
									variant="text"
									theme="monochrome"
									shape="square"
									symbol="arrow_range"
									aria-label="Full width"
									aria-pressed={fullWidth()}
									onClick={toggleFullWidth}
								/>
								<Tooltip>Full width</Tooltip>
							</TooltipTrigger>
						</Show>
					</HStack>
				</HStack>
			</HStack>
			<div
				class="interactive-table-wrapper"
				classList={{
					"interactive-table-wrapper--has-x-scroll": hasXScroll(),
				}}
				ref={wrapperRef}
			>
				<table
					class="interactive-table"
					classList={{ "interactive-table--wrap-text": wrapText() }}
					style={tableStyle()}
					ref={tableRef}
				>
					<thead>
						<tr>
							<For each={props.headers}>
								{(header, i) => (
									<th
										ref={(el) => {
											if (el && isRichTableCell(header)) {
												const before = el.querySelector(
													".csv-col-resize-proxy",
												);
												mountRichCellNodes(el, header.nodes, before);
											}
										}}
										style={colStyle(i())}
										classList={{
											"csv-th-resizing": resizing() === i(),
											...formatClassList(columnFormats()[i()]),
										}}
									>
										<Show when={!isRichTableCell(header)}>
											{tableCellText(header)}
										</Show>
										<Show when={i() > 0}>
											<div
												class="csv-col-resize-proxy"
												onMouseEnter={() => setHoveredHandle(i() - 1)}
												onMouseLeave={() => setHoveredHandle(null)}
												onMouseDown={(e) => onResizeStart(i() - 1, e)}
												onTouchStart={(e) => onTouchResizeStart(i() - 1, e)}
												onDblClick={(e) => onResizeReset(i() - 1, e)}
											/>
										</Show>
										<div
											class="csv-col-resize-handle"
											classList={{
												"csv-col-resize-highlight":
													hoveredHandle() === i() || resizing() === i(),
											}}
											style={
												resizing() === i() && dragHeight()
													? { height: `${dragHeight()}px` }
													: undefined
											}
											onMouseEnter={() => setHoveredHandle(i())}
											onMouseLeave={() => setHoveredHandle(null)}
											onMouseDown={(e) => onResizeStart(i(), e)}
											onTouchStart={(e) => onTouchResizeStart(i(), e)}
											onDblClick={(e) => onResizeReset(i(), e)}
										/>
									</th>
								)}
							</For>
						</tr>
					</thead>
					<tbody>
						<For each={props.rows}>
							{(row) => (
								<tr>
									<For each={row}>
										{(cell, i) => (
											<TableBodyCell
												cell={cell}
												style={colStyle(i())}
												format={columnFormats()[i()]}
											/>
										)}
									</For>
								</tr>
							)}
						</For>
					</tbody>
				</table>
			</div>
			<Show when={props.truncationMessage}>
				<p class="interactive-table-truncated">{props.truncationMessage}</p>
			</Show>
		</VStack>
	);
};
